"use client";

import { useEffect, useState } from "react";
import { api } from "@/lib/mobile/api";
import { KaitMark } from "../_mobile/icons";
import { KaitMark as DeskMark, KaitWordmark, MltMark } from "../_desk/Brand";
import { ThemeProvider } from "../operator/theme";

// Общий вход (12.10): `app.klait.ru/login`. Человек вводит логин и пароль, не
// зная адреса ресторана, — ресторан находит сервер (`loginAny`). Пропуска эта
// страница себе не берёт: работать человек будет на адресе ресторана, и туда
// его уводит ссылка с одноразовым кодом перехода. Здесь запоминается только,
// куда он ушёл в прошлый раз, — чтобы предложить «Продолжить».

type Choice = { tenantId: string; slug: string; name: string; logo: string | null; network: string | null };
type Entered = { handoffUrl: string; restaurant?: { name: string } };
// Пропуск в теле получает только нативное приложение — эта страница его не
// увидит, но тип ответа сервера включает и эту ветку.
type NativeEntered = { token: string; restaurant: { slug: string; name: string } };
type Answer = Entered | NativeEntered | { choose: Choice[]; ticket: string };

const LAST_KEY = "klait_last_restaurant";
type Last = { name: string; url: string };

function readLast(): Last | null {
  try {
    const raw = window.localStorage.getItem(LAST_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<Last>;
    return typeof v.name === "string" && typeof v.url === "string" ? { name: v.name, url: v.url } : null;
  } catch {
    return null;
  }
}

function rememberLast(name: string, handoffUrl: string): void {
  try {
    const u = new URL(handoffUrl);
    window.localStorage.setItem(LAST_KEY, JSON.stringify({ name, url: `${u.origin}${u.pathname}` }));
  } catch {
    // Хранилище недоступно (приватный режим) — просто не предложим «Продолжить».
  }
}

type Surface = "app" | "desk";
const WIDE = "(min-width: 900px)";

/**
 * Широкий экран — вход как у рабочего места, узкий — как у приложения. Порог
 * тот же, что выбирает, куда вести после входа. До монтирования ширина
 * неизвестна — null, страница ждёт один кадр.
 */
function useWide(): boolean | null {
  const [wide, setWide] = useState<boolean | null>(null);
  useEffect(() => {
    const mq = window.matchMedia(WIDE);
    const on = () => setWide(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return wide;
}

export default function PlatformLoginPage() {
  const wide = useWide();
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [choice, setChoice] = useState<{ list: Choice[]; ticket: string } | null>(null);
  const [last, setLast] = useState<Last | null>(null);
  // Вид входа и куда вести после него — по ширине экрана, без выбора
  // (решение 12.10: экран сам знает, куда человеку надо).
  const target: Surface = wide ? "desk" : "app";

  useEffect(() => {
    setLast(readLast());
  }, []);

  function go(handoffUrl: string, name: string) {
    rememberLast(name, handoffUrl);
    window.location.assign(handoffUrl);
  }

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy) return;
    // Значения — из самих полей, а не из состояния: Chrome рисует
    // подставленный сохранённый пароль, но не сообщает о нём странице, пока
    // человек по ней не кликнет, — состояние пустое, хотя поля заполнены
    // (12.10, кнопка «Войти» стояла серой).
    const f = new FormData(e.currentTarget);
    const loginValue = String(f.get("login") ?? login).trim();
    const passwordValue = String(f.get("password") ?? password);
    if (!loginValue || !passwordValue) {
      setErr("Введите логин и пароль");
      return;
    }
    setBusy(true);
    setErr("");
    const r = await api<Answer>("loginAny", { login: loginValue, password: passwordValue, surface: target });
    setBusy(false);
    if (!r.ok) {
      setErr(r.error);
      return;
    }
    if ("handoffUrl" in r.data) {
      go(r.data.handoffUrl, r.data.restaurant?.name ?? hostName(r.data.handoffUrl));
      return;
    }
    if (!("choose" in r.data)) return;
    setPassword("");
    setChoice({ list: r.data.choose, ticket: r.data.ticket });
  }

  async function pick(c: Choice) {
    if (busy || !choice) return;
    setBusy(true);
    setErr("");
    const r = await api<Entered | NativeEntered>("loginPick", { ticket: choice.ticket, tenantId: c.tenantId, surface: target });
    setBusy(false);
    if (!r.ok) {
      setErr(r.error);
      setChoice(null);
      return;
    }
    if ("handoffUrl" in r.data) go(r.data.handoffUrl, c.name);
  }

  const view: ViewProps = {
    login,
    setLogin,
    password,
    setPassword,
    busy,
    err,
    choice,
    last,
    target,
    onSubmit: submit,
    onPick: (c) => void pick(c),
    onBack: () => setChoice(null),
  };

  if (wide === null) return <div className="min-h-dvh bg-[#0b0c0c]" />;
  return target === "desk" ? <DeskView {...view} /> : <MobileView {...view} />;
}

type ViewProps = {
  login: string;
  setLogin: (v: string) => void;
  password: string;
  setPassword: (v: string) => void;
  busy: boolean;
  err: string;
  choice: { list: Choice[]; ticket: string } | null;
  last: Last | null;
  target: Surface;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  onPick: (c: Choice) => void;
  onBack: () => void;
};

function choiceLabel(c: Choice): string {
  return c.network ? `${c.name} · ${c.network}` : c.name;
}

/** Телефон: вход как у приложения (/app). */
function MobileView(v: ViewProps) {
  const ghost = { marginTop: 10, background: "var(--control)", color: "var(--ink)" } as const;
  const link = { background: "none", border: 0, marginTop: 14, textDecoration: "underline" } as const;
  return (
    <div className="zeit zeit3">
      <div className="z-page z3-gate-page">
        <div className="z3-gate">
          <div className="z3-gate-top">
            <span className="z3-gate-mark" aria-hidden>
              <KaitMark />
            </span>
          </div>
          <div className="z3-gate-plate">
            <div className="z-login-logo">KLAIT</div>
            {v.choice ? (
              <>
                <p className="z3-forgot" style={{ marginTop: 0 }}>
                  Вы работаете в нескольких местах. Куда войти?
                </p>
                {v.choice.list.map((c) => (
                  <button key={c.tenantId} type="button" className="z-btn" style={ghost} disabled={v.busy} onClick={() => v.onPick(c)}>
                    {choiceLabel(c)}
                  </button>
                ))}
                {v.err && <div className="z-err">{v.err}</div>}
                <button type="button" className="z3-forgot" style={link} onClick={v.onBack}>
                  Войти под другим логином
                </button>
              </>
            ) : (
              <>
                <form onSubmit={v.onSubmit}>
                  <div className="z-field">
                    <label>Логин</label>
                    <input
                      name="login"
                      value={v.login}
                      onChange={(e) => v.setLogin(e.target.value)}
                      placeholder="почта или телефон"
                      autoComplete="username"
                      autoCapitalize="none"
                    />
                  </div>
                  <div className="z-field">
                    <label>Пароль</label>
                    <input
                      name="password"
                      type="password"
                      value={v.password}
                      onChange={(e) => v.setPassword(e.target.value)}
                      placeholder="••••••••"
                      autoComplete="current-password"
                    />
                  </div>
                  {v.err && <div className="z-err">{v.err}</div>}
                  <button className="z-btn" style={{ marginTop: 18 }} disabled={v.busy}>
                    {v.busy ? "Входим…" : "Войти"}
                  </button>
                </form>
                {v.last && (
                  <button type="button" className="z-btn" style={ghost} onClick={() => window.location.assign(v.last!.url)}>
                    Продолжить в {v.last.name}
                  </button>
                )}
                <p className="z3-forgot">Забыли пароль? Попросите руководителя сбросить его — новый придёт от него.</p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Компьютер: вход как у рабочего места (/desk) — тёмная сцена, карточка справа. */
function DeskView(v: ViewProps) {
  const [now, setNow] = useState<Date | null>(null);
  useEffect(() => {
    setNow(new Date());
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const field =
    "h-[clamp(3rem,2.9167vw,3.5rem)] w-full rounded-full border border-white/10 bg-white/[0.04] px-6 text-[16px] text-white outline-none transition-colors placeholder:text-white/35 focus:border-[var(--op-lime)]";
  const primary =
    "h-[clamp(3rem,2.9167vw,3.5rem)] w-full rounded-full bg-[var(--op-lime)] text-[16px] font-semibold text-white transition-[opacity,box-shadow] duration-150 [box-shadow:0_10px_40px_-12px_var(--op-lime)] hover:opacity-95 disabled:opacity-40";
  const ghost =
    "h-[clamp(3rem,2.9167vw,3.5rem)] w-full rounded-full border border-white/15 bg-white/[0.06] px-6 text-[15px] text-white/85 transition-colors hover:bg-white/[0.1] disabled:opacity-40";
  const quiet = "text-[13px] text-white/45 underline-offset-4 transition-colors hover:text-white/80 hover:underline";
  return (
    <ThemeProvider fontClass="">
      <main className="relative grid min-h-dvh overflow-hidden bg-[#0b0c0c] p-6 min-[900px]:grid-cols-2">
        <span aria-hidden className="pointer-events-none absolute -left-24 top-1/2 hidden w-[760px] -translate-y-1/2 opacity-[0.06] min-[900px]:block">
          <DeskMark className="w-full text-white" />
        </span>
        <section className="relative hidden flex-col justify-center px-16 min-[900px]:flex">
          <div className="text-[clamp(64px,4.7917vw,92px)] font-light leading-none tabular-nums text-white/85">
            {now ? now.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" }) : "\u00A0"}
          </div>
          <div className="mt-4 text-[17px] capitalize text-white/45">
            {now ? now.toLocaleDateString("ru-RU", { weekday: "long", day: "numeric", month: "long" }) : "\u00A0"}
          </div>
        </section>
        <section className="relative grid place-items-center min-[900px]:pr-24">
          <div className="w-full max-w-[min(100%,clamp(400px,26.04vw,500px))] rounded-2xl border border-white/10 bg-white/[0.04] p-[clamp(2rem,2.2917vw,2.75rem)] backdrop-blur-2xl [box-shadow:0_40px_120px_-40px_rgba(0,0,0,0.9)]">
            <div className="flex items-center gap-2.5">
              <DeskMark className="h-5 w-auto shrink-0 text-white" />
              <KaitWordmark className="text-white" />
            </div>
            {v.choice ? (
              <div className="mt-9 space-y-3">
                <p className="text-[14px] text-white/60">Вы работаете в нескольких местах. Куда войти?</p>
                {v.choice.list.map((c) => (
                  <button key={c.tenantId} type="button" className={ghost} disabled={v.busy} onClick={() => v.onPick(c)}>
                    {choiceLabel(c)}
                  </button>
                ))}
                {v.err && <p className="rounded-2xl bg-[var(--op-danger)]/15 px-4 py-2.5 text-[13px] text-[#ff9a8d]">{v.err}</p>}
                <button type="button" className={`${quiet} mt-2`} onClick={v.onBack}>
                  Войти под другим логином
                </button>
              </div>
            ) : (
              <>
                <form onSubmit={v.onSubmit}>
                  <div className="mt-9 space-y-3">
                    <input
                      name="login"
                      className={field}
                      value={v.login}
                      onChange={(e) => v.setLogin(e.target.value)}
                      autoFocus
                      autoComplete="username"
                      placeholder="Email или телефон"
                      aria-label="Email или телефон"
                    />
                    <input
                      name="password"
                      className={field}
                      type="password"
                      value={v.password}
                      onChange={(e) => v.setPassword(e.target.value)}
                      autoComplete="current-password"
                      placeholder="Пароль"
                      aria-label="Пароль"
                    />
                  </div>
                  {v.err && <p className="mt-4 rounded-2xl bg-[var(--op-danger)]/15 px-4 py-2.5 text-[13px] text-[#ff9a8d]">{v.err}</p>}
                  <button className={`${primary} mt-7`} disabled={v.busy} type="submit">
                    {v.busy ? "Вхожу…" : "Войти"}
                  </button>
                </form>
                {v.last && (
                  <button type="button" className={`${ghost} mt-3`} onClick={() => window.location.assign(v.last!.url)}>
                    Продолжить в {v.last.name}
                  </button>
                )}
              </>
            )}
          </div>
        </section>
        <div className="pointer-events-none absolute bottom-8 left-1/2 flex -translate-x-1/2 flex-col items-center gap-3">
          <span className="text-[11px] uppercase tracking-[0.32em] text-white/30">разработано</span>
          <MltMark className="h-7 w-auto opacity-60" />
        </div>
      </main>
    </ThemeProvider>
  );
}

/** Имя для «Продолжить в …», когда ресторан был один и названия в ответе нет. */
function hostName(url: string): string {
  try {
    const u = new URL(url);
    const seg = u.pathname.split("/")[1] ?? "";
    // Сеть: `pinski.klait.ru/hide/app` — ресторан в пути; иначе — в хосте.
    return seg && seg !== "app" && seg !== "desk" ? seg : u.hostname.split(".")[0];
  } catch {
    return "ресторане";
  }
}
